import { useState, useEffect, useRef } from 'react'
import { getAllSearchableNames } from '../data/guests'

export interface AutocompleteOption {
  id: string
  name: string
  partyName: string
  isExactMatch: boolean
}

export function useAutocomplete(query: string, minLength: number = 2) {
  const [suggestions, setSuggestions] = useState<AutocompleteOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [allNames, setAllNames] = useState<Array<{
    name: string
    partyName: string
    partyId: string
    memberId: string
  }>>([])
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Load all names on mount and when refresh is triggered
  useEffect(() => {
    let isCancelled = false
    
    const loadNames = async () => {
      try {
        setIsLoading(true)
        const names = await getAllSearchableNames()
        
        // Only update state if the effect hasn't been cancelled
        if (!isCancelled) {
          setAllNames(names)
        }
      } catch (error) {
        console.error('Failed to load searchable names:', error)
        // Show user-friendly error - could set an error state here if needed
        if (!isCancelled) {
          setAllNames([]) // Ensure we have an empty array to work with
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }
    
    loadNames()
    
    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true
    }
  }, [refreshTrigger])

  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    if (!query || query.length < minLength) {
      setSuggestions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    
    // Debounced search
    debounceTimeoutRef.current = setTimeout(() => {
      const searchQuery = query.toLowerCase().trim()
      
      // Find matching names
      const matches = allNames.filter(option => {
        const name = option.name.toLowerCase()
        return name.includes(searchQuery)
      })

      // Sort by relevance: exact matches first, then starts with, then contains
      const sortedMatches = matches.sort((a, b) => {
        const aName = a.name.toLowerCase()
        const bName = b.name.toLowerCase()
        
        // Exact match
        if (aName === searchQuery) return -1
        if (bName === searchQuery) return 1
        
        // Starts with query
        if (aName.startsWith(searchQuery) && !bName.startsWith(searchQuery)) return -1
        if (bName.startsWith(searchQuery) && !aName.startsWith(searchQuery)) return 1
        
        // Alphabetical for same relevance level
        return aName.localeCompare(bName)
      })

      // Mark exact matches and limit results
      const finalSuggestions = sortedMatches.slice(0, 30).map(option => ({
        id: option.memberId,
        name: option.name,
        partyName: option.partyName,
        isExactMatch: option.name.toLowerCase() === searchQuery
      }))

      setSuggestions(finalSuggestions)
      setIsLoading(false)
    }, 150)

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
      setIsLoading(false)
    }
  }, [query, minLength, allNames])

  const clearSuggestions = () => {
    setSuggestions([])
  }

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  return {
    suggestions,
    isLoading,
    clearSuggestions,
    refreshData,
    hasSuggestions: suggestions.length > 0
  }
}